import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  EventEmitter,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

// Modular D3 imports
import { select, pointer, Selection } from 'd3-selection';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { axisBottom, axisLeft, axisRight } from 'd3-axis';
import { line, curveMonotoneX } from 'd3-shape';
import 'd3-transition';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ThemeService, Theme } from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type colorLegend = `#${string}`;

export interface BarChartData {
  start: string;
  end: string;
  month_short?: string;
  [key: string]: any;
}

export interface LegendData {
  label: string;
  color: colorLegend;
  type?: 'circle' | 'line' | 'dashed-line' | 'square' | 'square-outline';
}

export interface ColorMap {
  data1: colorLegend | '';
}

export interface TooltipConfig {
  label: string;
  key: string;
  type?: 'percent' | string;
  prefix?: string;
}

export interface LineConfig {
  key: string;
  label: string;
  color: string;
  strokeWidth?: number;
  axis?: 'left' | 'right';
  fill?: boolean;
  type?: 'solid' | 'dashed';
}

@Component({
  selector: 'app-bar-line',
  standalone: true,
  template: `
    <div #chartContainer class="w-full h-full relative" [class.bg-slate-50]="showBackground && currentTheme === 'light'" [class.bg-slate-800]="showBackground && currentTheme === 'dark'"></div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe]
})
export class BarLineComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  @Input() data1: BarChartData[] = [];
  @Input() legendData: LegendData[] = [];
  @Input() hideLegend: boolean = false;
  @Input() interactiveLegends: boolean = true;
  @Output() legendClick = new EventEmitter<number>();

  hiddenIndices = new Set<number>();

  toggleLegend(index: number) {
    if (!this.interactiveLegends) return;
    if (this.hiddenIndices.has(index)) {
      this.hiddenIndices.delete(index);
    } else {
      this.hiddenIndices.add(index);
    }
    this.legendClick.emit(index);
    this.renderChart();
  }

  @Input() margin: { top: number; right: number; bottom: number; left: number } | null = null;
  @Input() showBackground: boolean = true;
  @Input() height: number | null = null; // Optional height override
  @Input() currentTheme: Theme = 'light';

  private defaultMargin = { top: 30, right: 0, bottom: 70, left: 80 };

  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private combinedData: (BarChartData & { color: colorLegend })[][] = [];
  fullBarWidth: number = 0;
  fullBarHeight: number = 0;

  @Input() colors: ColorMap = { data1: '' };
  @Input() valueKey: string = 'total';
  @Input() otherKey: string = ''; // Legacy support and simple 2nd series
  @Input() valueType: '$' | '%' | '' = '%';
  @Input() addGradient: boolean = true;
  @Input() barTextposition: 'top' | 'inside' | 'none' | 'center' = 'top';
  @Input() barTextColor: colorLegend = '' as colorLegend;
  @Input() tooltipData: TooltipConfig[] = [
    { label: 'Dental', key: 'dental' },
    { label: 'HealthCare', key: 'healthcare' },
    { label: 'Non HealthCare', key: 'non_healthcare' },
  ];
  @Input() labelKey: any = undefined;
  @Input() tooltipHeaderKey: string = '';
  @Input() lineColor: colorLegend = '' as colorLegend;
  @Input() flatBars: boolean = false;
  @Input() lines: LineConfig[] = [];
  @Input() leftAxisLabel: string = '';
  @Input() rightAxisLabel: string = '';
  @Input() rightAxisLabelOffset: number = 35;
  @Input() hideRightAxis: boolean = false;
  @Input() minBarWidth: number = 0;
  @Input() maxBarWidth: number = 0;
  @Input() rightAxisType: 'percent' | 'number' | '' = '';
  @Input() barLabel: string = 'Total';
  @Input() formatDateXAxis: boolean = false;
  @Input() showXGrid: boolean = false;
  @Input() showYGrid: boolean = true;
  @Input() legendGap: number = 0;
  @Input() unit: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month';
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() formatKM: boolean = true;
  @Input() wrapXAxisLabels: boolean = false;
  @Input() removeYearInXAxis: boolean = false;

  private themeService = inject(ThemeService);

  constructor(private datePipe: DatePipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      // Debounce or just timeout to allow view update
      setTimeout(() => {
        this.renderChart();
      }, 100);
    }
  }

  ngAfterViewInit() {
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      setTimeout(() => this.renderChart(), 0);
    });

    this.observeResize();
    // Initial render
    setTimeout(() => this.renderChart(), 100);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private getThemeColors() {
    return this.currentTheme === 'dark'
      ? {
        text: '#e2e8f0', // slate-200
        textSecondary: '#94a3b8', // slate-400
        grid: '#334155', // slate-700
        tooltipBg: '#1e293b', // slate-800
        tooltipBorder: '#475569', // slate-600
        tooltipText: '#f1f5f9', // slate-100
        tooltipSecondaryText: '#cbd5e1', // slate-300
        tooltipDivider: '#334155', // slate-700
        legendText: '#e2e8f0', // slate-200
      }
      : {
        text: '#334155', // slate-700
        textSecondary: '#64748b', // slate-500
        grid: '#e2e8f0', // slate-200
        tooltipBg: '#ffffff', // white
        tooltipBorder: '#e2e8f0', // slate-200
        tooltipText: '#1e293b', // slate-800
        tooltipSecondaryText: '#64748b', // slate-500
        tooltipDivider: '#f1f5f9', // slate-100
        legendText: '#475569', // slate-600
      };
  }

  private observeResize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      this.fullBarWidth = entries[0].contentRect.width;
      this.fullBarHeight = entries[0].contentRect.height;
      this.renderChart();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  private clearChart() {
    if (this.chartContainer) {
      select(this.chartContainer.nativeElement).select('svg').remove();
      select(this.chartContainer.nativeElement).select('.chart-tooltip').remove();
    }
  }

  private renderChart() {
    if (!this.chartContainer || !this.data1) return;

    const element = this.chartContainer.nativeElement;
    this.fullBarWidth = element.clientWidth || element.parentElement?.clientWidth || 0;
    this.fullBarHeight = this.height || element.clientHeight || 350;

    const currentMargin = this.margin || this.defaultMargin;

    if (this.fullBarWidth === 0 || this.fullBarHeight === 0) {
      return;
    }

    const { data1, combinedData } = this.combineData();
    this.combinedData = combinedData;

    if (!data1 || data1.length === 0) {
      this.clearChart();
      return;
    }

    this.clearChart();

    const width = this.fullBarWidth - currentMargin.left - currentMargin.right;
    const height = this.fullBarHeight - currentMargin.top - currentMargin.bottom;

    const mainSvg = select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('width', width + currentMargin.left + currentMargin.right)
      .attr('height', height + currentMargin.top + currentMargin.bottom);

    const svg = mainSvg.append('g')
      .attr('transform', `translate(${currentMargin.left},${currentMargin.top})`);

    // --- X Axis ---
    const x = scaleBand()
      .range([0, width])
      .domain(data1.map((d: any) => d[this.labelKey || 'month_short']))
      .padding(0.2);

    // --- Y Axis (Left) ---
    // If lines are on the left axis, we must consider them for the max scale
    let maxY = max(data1, (d: any) => +d[this.valueKey]) || 0;

    const leftAxisLines = this.lines.filter(l => !l.axis || l.axis === 'left');
    if (leftAxisLines.length > 0) {
      const maxLinesLeft = max(data1, (d: any) => {
        return max(leftAxisLines, l => +d[l.key] || 0);
      }) || 0;
      maxY = Math.max(maxY, maxLinesLeft);
    }

    if (this.otherKey && !this.lines.length) {
      const maxOther = max(data1, (d: any) => +d[this.otherKey]) || 0;
      maxY = Math.max(maxY, maxOther);
    }

    // Add buffer and ensure it's at least 1 to avoid degenerate scales
    maxY = Math.max(maxY * 1.1, 1);

    const y = scaleLinear().domain([0, maxY]).range([height, 0]);

    this.drawAxes(svg, x, y, width, height, currentMargin);

    // --- Background Bars ---
    if (this.showBackground && !this.hiddenIndices.has(0)) {
      svg
        .selectAll('.bar-bg')
        .data(data1)
        .enter()
        .append('path')
        .attr('class', 'bar-bg')
        .attr('d', (d: any) =>
          this.drawRoundedBar(
            x(d[this.labelKey || 'month_short'])!,
            0,
            x.bandwidth(),
            height,
            4,
            true,
            true
          )
        )
        .attr('fill', this.currentTheme === 'dark' ? '#334155' : '#f1f5f9')
        .style('opacity', 0.5);
    }

    const colors = this.getThemeColors();

    if (!this.hiddenIndices.has(0)) {
      const barGroups = svg
        .selectAll('.bar-group')
        .data(this.combinedData)
        .enter()
        .append('g')
        .attr('class', 'bar-cluster')
        .attr('transform', (d: any, i: any) => {
          const xVal = x(data1[i][this.labelKey || 'month_short']);
          return `translate(${xVal}, 0)`;
        });

      let bandwidth = x.bandwidth();
      if (this.minBarWidth > 0 && bandwidth < this.minBarWidth) {
        bandwidth = this.minBarWidth;
      }
      if (this.maxBarWidth > 0 && bandwidth > this.maxBarWidth) {
        bandwidth = this.maxBarWidth;
      }
      const barOffset = (x.bandwidth() - bandwidth) / 2;

      barGroups
        .selectAll('path')
        .data((d: any) => d)
        .enter()
        .append('path')
        .attr('d', (d: any) =>
          this.drawRoundedBar(
            barOffset,
            y(0),
            bandwidth,
            0,
            4,
            true,
            true
          )
        )
        .attr('fill', (d: any) => {
          if (this.addGradient) {
            return `url(#gradient-${d.color.replace('#', '')})`;
          }
          return d.color;
        })
        .transition()
        .duration(1000)
        .attr('d', (d: any) => {
          let val = d[this.valueKey];
          // Simple legacy support: if valueKey missing, try otherKey?
          // No, usually valueKey is primary.

          const barHeight = height - y(val || 0);
          return this.drawRoundedBar(
            barOffset,
            y(val || 0),
            bandwidth,
            barHeight,
            4,
            true,
            true
          );
        });

      // --- Bar Text ---
      if (this.barTextposition !== 'none') {
        barGroups
          .selectAll('text')
          .data((d: any) => d)
          .enter()
          .append('text')
          .text((d: any) => {
            const val = d[this.valueKey];
            if (this.valueType === '$') {
              return `$${(val || 0).toLocaleString()}`;
            }
            if (this.valueType === '') {
              return (val || 0).toLocaleString();
            }
            return `${(val || 0).toLocaleString()}${this.valueType}`;
          })
          .attr('x', bandwidth / 2 + barOffset)
          .attr('y', (d: any) => {
            const val = d[this.valueKey] || 0;
            if (this.barTextposition === 'top') return y(val) - 5;
            if (this.barTextposition === 'inside') return y(val) + 20;
            return y(val) - 5;
          })
          .attr('text-anchor', 'middle')
          .style('font-size', '12px')
          .style('font-family', 'Lato, sans-serif')
          .style('fill', this.barTextColor || colors.text)
          .style('opacity', 0)
          .transition()
          .delay(800)
          .style('opacity', 1);
      }

      // --- Tooltip ---
      barGroups
        .append('rect')
        .attr('x', barOffset)
        .attr('width', bandwidth)
        .attr('height', height)
        .attr('fill', 'transparent')
        .on('mouseover', (event: any, d: any) => {
          const dataItem = d && d.length ? d[0] : d;
          const tooltipHtml = this.getToolTipHtml(dataItem, this.tooltipData, this.datePipe, colors);

          select('.chart-tooltip').remove();
          const tooltip = select(this.chartContainer.nativeElement)
            .append('div')
            .attr('class', 'chart-tooltip')
            .style('position', 'absolute')
            .style('z-index', '1000') // Ensure tooltip is on top
            .style('visibility', 'visible')
            .html(tooltipHtml);

          const tooltipNode = tooltip.node() as HTMLElement;
          if (tooltipNode) {
            const tooltipRect = tooltipNode.getBoundingClientRect();
            const containerRect = this.chartContainer.nativeElement.getBoundingClientRect();
            const parentRect = this.chartContainer.nativeElement.parentElement?.getBoundingClientRect();
            const effectiveHeight = parentRect && parentRect.height < containerRect.height ? parentRect.height : containerRect.height;

            let left = event.clientX - containerRect.left + 15;
            let top = event.clientY - containerRect.top - 15;
            if (left + tooltipRect.width > containerRect.width) {
              left = event.clientX - containerRect.left - tooltipRect.width - 15;
            }
            const overflowBottom = top + tooltipRect.height > effectiveHeight - 10 || event.clientY + tooltipRect.height > window.innerHeight;
            if (overflowBottom || event.clientY - containerRect.top > effectiveHeight / 2) {
              top = event.clientY - containerRect.top - tooltipRect.height - 15;
            }
            if (top < 0) {
              top = 0;
            }
            tooltip.style('top', `${top}px`).style('left', `${left}px`);
          }
        })
        .on('mousemove', (event: any) => {
          const tooltip = select(this.chartContainer.nativeElement).select('.chart-tooltip');
          const tooltipNode = tooltip.node() as HTMLElement;
          if (!tooltipNode) return;
          const tooltipRect = tooltipNode.getBoundingClientRect();
          const containerRect = this.chartContainer.nativeElement.getBoundingClientRect();
          const parentRect = this.chartContainer.nativeElement.parentElement?.getBoundingClientRect();
          const effectiveHeight = parentRect && parentRect.height < containerRect.height ? parentRect.height : containerRect.height;

          let left = event.clientX - containerRect.left + 10;
          let top = event.clientY - containerRect.top - 10;
          if (left + tooltipRect.width > containerRect.width) {
            left = event.clientX - containerRect.left - tooltipRect.width - 10;
          }
          const overflowBottom = top + tooltipRect.height > effectiveHeight - 10 || event.clientY + tooltipRect.height > window.innerHeight;
          if (overflowBottom || event.clientY - containerRect.top > effectiveHeight / 2) {
            top = event.clientY - containerRect.top - tooltipRect.height - 10;
          }
          if (top < 0) {
            top = 0;
          }
          tooltip.style('top', `${top}px`).style('left', `${left}px`);
        })
        .on('mouseout', () => {
          select('.chart-tooltip').remove();
        });

      if (this.addGradient) {
        this.appendDefs(svg, this.combinedData);
      }
    }

    // --- Lines ---
    // Combined logic: use `this.lines` OR `this.otherKey` (legacy wrapper)
    const linesToDraw = [...this.lines];
    if (this.otherKey && linesToDraw.length === 0) {
      // Construct a line config from otherKey and lineColor
      linesToDraw.push({
        key: this.otherKey,
        label: 'Other', // Label might need to be passed or generic
        color: this.lineColor || '#000',
        axis: 'right' // Assume right for legacy otherKey usually (retention rate)
      });
    }

    const visibleLines = linesToDraw.filter((l, i) => !this.hiddenIndices.has(i + 1));

    if (visibleLines.length > 0) {
      const rightAxisLines = visibleLines.filter(l => l.axis === 'right');

      let yRight: ScaleLinear<number, number> | null = null;
      if (rightAxisLines.length > 0) {
        const maxRight = max(data1, (d: any) => {
          return max(rightAxisLines, l => +d[l.key] || 0);
        }) || 0;
        const maxVal = this.rightAxisType === 'percent' ? 100 : (maxRight * 1.1);
        yRight = scaleLinear()
          .domain([0, maxVal])
          .range([height, 0]);
      }

      visibleLines.forEach(lineConfig => {
        const targetY = (lineConfig.axis === 'right' && yRight) ? yRight : y;

        const lineGenerator = line<any>()
          .x((d: any) => x(d[this.labelKey || 'month_short'])! + x.bandwidth() / 2)
          .y((d: any) => targetY(d[lineConfig.key]))
          .curve(curveMonotoneX);

        svg.append('path')
          .datum(data1)
          .attr('fill', 'none')
          .attr('stroke', lineConfig.color)
          .attr('stroke-width', lineConfig.strokeWidth || 2)
          .attr('stroke-dasharray', lineConfig.type === 'dashed' ? '5,5' : 'none')
          .attr('d', lineGenerator);

        svg.selectAll(`.point-${lineConfig.key}`)
          .data(data1)
          .enter()
          .append('circle')
          .attr('cx', (d: any) => x(d[this.labelKey || 'month_short'])! + x.bandwidth() / 2)
          .attr('cy', (d: any) => targetY(d[lineConfig.key]))
          .attr('r', 3)
          .attr('fill', lineConfig.color);
      });

      if (!this.hideRightAxis && yRight) {
        const axisR = axisRight(yRight)
          .ticks(5)
          .tickFormat((d: any) => {
            if (this.rightAxisType === 'percent') return `${d}%`;
            if (this.rightAxisType === 'number') {
              if (this.formatKM) {
                if (d >= 1000000) return `${(d / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
                if (d >= 1000) return `${(d / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
              }
              return d.toLocaleString();
            }
            return d.toLocaleString();
          });

        const gR = svg.append('g')
          .attr('class', 'y-axis-right')
          .attr('transform', `translate(${width}, 0)`)
          .call(axisR);

        gR.select('.domain')
          .attr('stroke', colors.grid)
          .attr('stroke-width', 1);
        gR.selectAll('text')
          .style('color', colors.textSecondary)
          .style('font-size', '12px')
          .style('font-family', 'Lato, sans-serif');

        if (this.rightAxisLabel) {
          svg.append('text')
            .attr('transform', `translate(${width + this.rightAxisLabelOffset}, ${height / 2}) rotate(90)`)
            .style('text-anchor', 'middle')
            .style('fill', colors.textSecondary)
            .style('font-size', '12px')
            .style('font-family', 'Lato, sans-serif')
            .style('letter-spacing', '0.4px')
            .text(this.rightAxisLabel);
        }
      }
    }

    // --- Legends ---
    const effectiveLegends = this.buildEffectiveLegends();
    if (effectiveLegends.length > 0) {
      this.appendLegends(mainSvg, height + currentMargin.top + currentMargin.bottom, colors, effectiveLegends);
    }
  }

  private combineData(): { data1: BarChartData[]; combinedData: any[] } {
    const data1 = this.data1;
    if (!data1) return { data1: [], combinedData: [] };

    const inputColors = this.colors;
    const defaultColors = ['#8b5cf6', '#f43f5e', '#10b981'];

    const combinedData = data1.map((d, i) => {
      const item = { ...d, color: inputColors.data1 || defaultColors[0] };
      return [item];
    });

    return { data1, combinedData };
  }

  private drawAxes(
    svg: Selection<SVGGElement, unknown, null, undefined>,
    x: ScaleBand<string>,
    y: ScaleLinear<number, number>,
    width: number,
    height: number,
    currentMargin: any
  ) {
    const colors = this.getThemeColors();

    const labels = x.domain();
    let tickValues = labels;
    if (this.xAxisShowOnlyFirstLast && labels.length > 0) {
      tickValues = [labels[0]];
      if (labels.length > 1) {
        tickValues.push(labels[labels.length - 1]);
      }
    } else if (labels.length > 8) {
      const step = Math.ceil(labels.length / 6);
      tickValues = labels.filter((_, i) => i % step === 0);
      if (!tickValues.includes(labels[labels.length - 1])) {
        tickValues.push(labels[labels.length - 1]);
      }
    }

    const xAxis = axisBottom(x)
      .tickSize(this.showXGrid ? -height : 0)
      .tickPadding(10)
      .tickValues(tickValues)
      .tickFormat((d: any) => {
        if (this.formatDateXAxis) {
          let parsed = dayjs(d);

          // Handle YYYY-WW format for weeks if dayjs fails to parse it correctly as a week
          if (this.unit === 'week' && typeof d === 'string' && d.length === 7 && d.includes('-')) {
            const parts = d.split('-');
            if (parts.length === 2 && parts[1].length === 2) {
              const year = parseInt(parts[0]);
              const week = parseInt(parts[1]);
              if (!isNaN(year) && !isNaN(week)) {
                parsed = dayjs().year(year).startOf('year').isoWeek(week).startOf('isoWeek');
              }
            }
          }

          if (!parsed.isValid()) return d;
          let isMultiYear = false;
          if (labels && labels.length > 1) {
            const firstDate = dayjs(labels[0]);
            const lastDate = dayjs(labels[labels.length - 1]);
            if (firstDate.isValid() && lastDate.isValid() && firstDate.year() !== lastDate.year()) {
              isMultiYear = true;
            }
          }
          if (this.unit === 'hour') return parsed.format('HH:mm');
          if (this.unit === 'week') {
            const weekEnd = parsed.add(6, 'day');
            if (this.removeYearInXAxis) {
              return `${parsed.format('DD MMM')} - ${weekEnd.format('DD MMM')}`;
            }
            return isMultiYear ? `${parsed.format('DD MMM YYYY')} - ${weekEnd.format('DD MMM YYYY')}` : `${parsed.format('MMM DD')} - ${weekEnd.format('MMM DD')}`;
          }
          if (this.unit === 'day') {
            if (this.removeYearInXAxis) {
              return parsed.format('DD MMM');
            }
            return isMultiYear ? parsed.format('MMM DD, YYYY') : parsed.format('MMM DD');
          }
          if (this.unit === 'quarter') return `Q${Math.floor(parsed.month() / 3) + 1} ${parsed.format('YYYY')}`;
          if (this.unit === 'year') return parsed.format('YYYY');
          return parsed.format('MMM YYYY');
        }
        return d;
      });

    const yAxisTicks = y.ticks(5).filter(Number.isInteger);

    const yAxis = axisLeft(y)
      .tickValues(this.valueType === '' ? yAxisTicks : y.ticks(5))
      .tickSize(this.showYGrid ? -width : 0)
      .tickFormat((d: any) => {
        if (this.valueType === '$') {
          if (this.formatKM) {
            if (d >= 1000000) return `$${(d / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
            if (d >= 1000) return `$${(d / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
          }
          return `$${d.toLocaleString()}`;
        }
        if (this.valueType === '') {
          if (this.formatKM) {
            if (d >= 1000000) return `${(d / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
            if (d >= 1000) return `${(d / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
          }
          return Math.round(d).toLocaleString();
        }
        return `${d.toLocaleString()}${this.valueType}`;
      });

    const gX = svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis);

    gX.select('.domain')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1); if (this.showXGrid) {
        gX.selectAll('line')
          .attr('stroke', colors.grid)
          .attr('stroke-dasharray', '3,3');
      }
    gX.selectAll('text')
      .style('color', colors.textSecondary)
      .style('font-size', '12px')
      .style('font-family', 'Lato, sans-serif');

    if (this.wrapXAxisLabels) {
      const maxBandWidth = x.bandwidth() || (width / Math.max(labels.length, 1));
      const targetWidth = Math.max(maxBandWidth * 1.15, 60);
      gX.selectAll('text').each(function () {
        const textEl = select(this);
        const rawText = textEl.text();
        if (!rawText) return;
        const words = rawText.split(/\s+/);
        if (words.length <= 1) return;

        textEl.text(null);
        let line: string[] = [];
        let lineNumber = 0;
        const lineHeight = 1.15;
        const yCoord = textEl.attr('y') || '9';
        let tspan = textEl.append('tspan').attr('x', 0).attr('y', yCoord).attr('dy', '0.71em');

        for (let i = 0; i < words.length; i++) {
          line.push(words[i]);
          tspan.text(line.join(' '));
          const node = tspan.node() as SVGTextContentElement | null;
          if (node && node.getComputedTextLength() > targetWidth && line.length > 1) {
            line.pop();
            tspan.text(line.join(' '));
            line = [words[i]];
            lineNumber++;
            tspan = textEl.append('tspan').attr('x', 0).attr('y', yCoord).attr('dy', `${lineNumber * lineHeight + 0.71}em`).text(words[i]);
          }
        }
      });
    }

    const gY = svg
      .append('g')
      .attr('class', 'y-axis')
      .call(yAxis);

    gY.select('.domain')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1);
    if (this.showYGrid) {
      gY.selectAll('line')
        .attr('stroke', colors.grid)
        .attr('stroke-dasharray', '3,3');
    }
    gY.selectAll('text')
      .style('color', colors.textSecondary)
      .style('font-size', '12px')
      .style('font-family', 'Lato, sans-serif');

    if (this.leftAxisLabel) {
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0 - currentMargin.left + 15)
        .attr('x', 0 - (height / 2))
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style('fill', colors.textSecondary)
        .style('font-size', '12px')
        .style('font-family', 'Lato, sans-serif')
        .style('letter-spacing', '0.4px')
        .text(this.leftAxisLabel);
    }
  }

  private appendDefs(svg: any, data: any[]) {
    const defs = svg.append('defs');
    const uniqueColors = new Set<string>();
    data.forEach(group => {
      group.forEach((d: any) => {
        if (d.color) uniqueColors.add(d.color);
      });
    });

    uniqueColors.forEach(color => {
      const gradientId = `gradient-${color.replace('#', '')}`;
      const gradient = defs
        .append('linearGradient')
        .attr('id', gradientId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', color)
        .attr('stop-opacity', 0.8);

      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', color)
        .attr('stop-opacity', 0.3);
    });
  }

  drawRoundedBar(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    roundTopLeft: boolean,
    roundTopRight: boolean
  ) {
    if (height < 0) height = 0;
    const right = x + width;
    const bottom = y + height;

    return [
      `M${x},${bottom}`,
      `V${y + (roundTopLeft ? radius : 0)}`,
      roundTopLeft
        ? `a${radius},${radius} 0 0 1 ${radius},-${radius}`
        : `V${y}`,
      `H${right - (roundTopRight ? radius : 0)}`,
      roundTopRight
        ? `a${radius},${radius} 0 0 1 ${radius},${radius}`
        : `H${right}`,
      `V${bottom}`,
      `H${x}`,
      `Z`,
    ].join(' ');
  }

  private buildEffectiveLegends(): LegendData[] {
    if (this.hideLegend) return [];
    if (this.legendData && this.legendData.length > 0) {
      return this.legendData;
    }
    // Auto-build from bar series + lines
    const items: LegendData[] = [];
    if (this.lines.length > 0) {
      const barColor = (this.colors.data1 || '#8b5cf6') as colorLegend;
      items.push({ label: this.barLabel, color: barColor });
      this.lines.forEach(l => {
        items.push({ label: l.label, color: l.color as colorLegend });
      });
    }
    return items;
  }

  appendLegends(
    svg: any,
    totalHeight: number,
    colors: ReturnType<typeof this.getThemeColors>,
    legendData?: LegendData[]
  ) {
    const data = legendData || this.legendData;
    if (!data || data.length === 0) {
      return;
    }

    const itemWidth = 160 + (this.legendGap || 0);
    const totalLegendWidth = data.length * itemWidth;
    const svgWidth = this.fullBarWidth;
    const startX = Math.max(0, (svgWidth - totalLegendWidth) / 2);

    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${startX}, ${totalHeight - 18})`);

    const legendItem = legend
      .selectAll('.legend-item')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d: any, i: any) => `translate(${i * itemWidth}, 0)`);

    legendItem.each(function (this: any, d: any, i: any) {
      const item = select(this);

      const parentChart = this.ownerSVGElement?.parentNode?.host || this; // fallback

      if (d.type === 'line' || d.type === 'dashed-line') {
        item.append('line')
          .attr('x1', 0)
          .attr('y1', 6)
          .attr('x2', 18)
          .attr('y2', 6)
          .attr('stroke', d.color)
          .attr('stroke-width', 2)
          .style('stroke-dasharray', d.type === 'dashed-line' ? '3,3' : 'none');
      } else if (d.type === 'square' || d.type === 'square-outline') {
        item.append('rect')
          .attr('x', 0)
          .attr('y', 0)
          .attr('width', 12)
          .attr('height', 12)
          .attr('rx', 2)
          .attr('fill', d.type === 'square' ? d.color : 'transparent')
          .attr('stroke', d.color)
          .attr('stroke-width', d.type === 'square-outline' ? 2 : 0);
      } else {
        item.append('circle')
          .attr('cx', 6)
          .attr('cy', 6)
          .attr('r', 5)
          .attr('fill', d.color);
      }
    });

    legendItem
      .style('cursor', 'pointer')
      .style('opacity', (d: any, i: number) => this.hiddenIndices.has(i) ? 0.4 : 1)
      .on('click', (event: any, d: any) => {
        const i = data.indexOf(d);
        this.toggleLegend(i);
      });

    legendItem
      .append('text')
      .attr('x', (d: any) => (d.type === 'line' || d.type === 'dashed-line') ? 25 : 20)
      .attr('y', 10)
      .text((d: any) => d.label)
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('fill', colors.legendText)
      .style('font-family', 'Lato, sans-serif');
  }

  getToolTipHtml(
    bar: BarChartData & { color: colorLegend },
    tooltipData: TooltipConfig[],
    datePipe: DatePipe,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const labelValue = (this.tooltipHeaderKey && (bar as any)[this.tooltipHeaderKey])
      || (bar as any)[this.labelKey || 'start']
      || (bar as any).start
      || (bar as any).unit_date;
    let headerText = '';

    if (this.formatDateXAxis && labelValue) {
      let parsed = dayjs(labelValue);

      // Handle YYYY-WW format for weeks
      if (this.unit === 'week' && typeof labelValue === 'string' && labelValue.length === 7 && labelValue.includes('-')) {
        const parts = labelValue.split('-');
        if (parts.length === 2 && parts[1].length === 2) {
          const year = parseInt(parts[0]);
          const week = parseInt(parts[1]);
          if (!isNaN(year) && !isNaN(week)) {
            parsed = dayjs().year(year).startOf('year').isoWeek(week).startOf('isoWeek');
          }
        }
      }

      if (parsed.isValid()) {
        if (this.unit === 'week') {
          const weekEnd = parsed.add(6, 'day');
          headerText = `${parsed.format('MMM DD')} - ${weekEnd.format('MMM DD, YYYY')}`;
        } else if (this.unit === 'day') {
          headerText = parsed.format('MMM DD, YYYY');
        } else if (this.unit === 'hour') {
          headerText = parsed.format('MMM DD, YYYY HH:mm');
        } else if (this.unit === 'quarter') {
          headerText = `Q${Math.floor(parsed.month() / 3) + 1} ${parsed.format('YYYY')}`;
        } else if (this.unit === 'year') {
          headerText = parsed.format('YYYY');
        } else {
          headerText = parsed.format('MMM YYYY');
        }
      } else {
        headerText = labelValue;
      }
    } else {
      try {
        headerText = labelValue ? datePipe.transform(labelValue, 'MMM yyyy') || labelValue : 'N/A';
      } catch (e) {
        headerText = labelValue || 'N/A';
      }
    }

    let html = `<div class="max-w-max p-2 rounded shadow-sm" style="
      background-color: ${colors.tooltipBg};
      border: 1px solid ${colors.tooltipBorder};
      padding: 0.5rem !important;
    ">
              <div style="border-bottom: 2px solid ${colors.tooltipDivider}; margin-bottom: 4px; padding-bottom: 4px;">
                <span class="w-2 inline-block h-2 rounded-full mr-1" style="background-color:${bar.color
      }"></span>
                <span style="color: ${colors.tooltipText}; font-weight: 600;">${headerText}</span>
              </div>`;

    tooltipData.forEach((item: any) => {
      if (bar[item.key] !== undefined || (bar as any)[item.key] === 0) {
        let val = (bar as any)[item.key];
        let formattedVal = '';

        if (val !== undefined && val !== null) {
          if (item.type === 'string') {
            formattedVal = (item.prefix || '') + val;
          } else {
            const numVal = +val;
            if (item.type === 'percent') {
              formattedVal = numVal.toFixed(1) + (item.prefix || '%');
            } else if (item.type === 'currency' || item.type === '$') {
              formattedVal = '$' + numVal.toLocaleString();
            } else if (item.type === 'count' || item.type === 'number') {
              formattedVal = Math.round(numVal).toLocaleString();
            } else {
              formattedVal = (item.prefix || '') + numVal.toLocaleString();
            }
          }
        } else {
          formattedVal = 'N/A';
        }

        html += `<div class="text-sm pt-1 flex items-center justify-between min-w-[120px]">
                  <span class="pr-2" style="color: ${colors.tooltipSecondaryText};">${item.label}</span>
                  <span class="font-bold text-right ml-auto" style="color: ${colors.tooltipText};">${formattedVal}</span>
                </div>`;
      }
    });

    return html + `</div>`;
  }
}